import { supabase } from "@/integrations/supabase/client";

export interface DashboardMetrics {
  totalDeals: number;
  totalDealValue: number;
  complianceRate: number;
  avgReviewTime: number;
  highRiskBlocked: number;
  activeAthletes: number;
  pendingReviews: number;
}

export interface DealTrend {
  date: string;
  count: number;
  value: number;
}

export interface SportDistribution {
  sport: string;
  count: number;
  value: number;
}

export interface RiskDistribution {
  riskLevel: string;
  count: number;
  percentage: number;
}

export async function getDashboardMetrics(): Promise<DashboardMetrics> {
  const { data: deals } = await supabase
    .from("deals" as any)
    .select("*");

  const { data: athletes } = await supabase
    .from("athletes" as any)
    .select("id");

  const totalDeals = (deals as any[])?.length || 0;
  const totalDealValue = (deals as any[])?.reduce((sum: number, deal: any) => sum + (deal.amount || 0), 0) || 0;
  
  const approvedDeals = (deals as any[])?.filter((d: any) => d.status === "active") || [];
  const complianceRate = totalDeals > 0 ? (approvedDeals.length / totalDeals) * 100 : 0;
  
  const highRiskBlocked = (deals as any[])?.filter(
    (d: any) => d.roc_risk_level === "critical" && d.status === "rejected"
  ).length || 0;

  const pendingReviews = (deals as any[])?.filter((d: any) => d.status === "pending_compliance").length || 0;

  // Calculate average review time (mock calculation)
  const avgReviewTime = 2.5; // days

  return {
    totalDeals,
    totalDealValue,
    complianceRate,
    avgReviewTime,
    highRiskBlocked,
    activeAthletes: athletes?.length || 0,
    pendingReviews,
  };
}

export async function getDealTrends(startDate: Date, endDate: Date): Promise<DealTrend[]> {
  const { data: deals } = await supabase
    .from("deals" as any)
    .select("created_at, amount")
    .gte("created_at", startDate.toISOString())
    .lte("created_at", endDate.toISOString())
    .order("created_at", { ascending: true });

  if (!deals) return [];

  // Group by date
  const trendMap = new Map<string, { count: number; value: number }>();
  
  deals.forEach((deal: any) => {
    const date = new Date(deal.created_at).toISOString().split('T')[0];
    const existing = trendMap.get(date) || { count: 0, value: 0 };
    trendMap.set(date, {
      count: existing.count + 1,
      value: existing.value + (deal.amount || 0),
    });
  });

  return Array.from(trendMap.entries()).map(([date, data]) => ({
    date,
    ...data,
  }));
}

export async function getSportDistribution(): Promise<SportDistribution[]> {
  const { data: deals } = await supabase
    .from("deals" as any)
    .select("athlete_id, amount");

  const { data: athletes } = await supabase
    .from("athletes" as any)
    .select("id, sport");

  if (!deals || !athletes) return [];

  const athleteMap = new Map(athletes.map((a: any) => [a.id, a.sport]));
  const sportMap = new Map<string, { count: number; value: number }>();

  deals.forEach((deal: any) => {
    const sport = athleteMap.get(deal.athlete_id) || "Unknown";
    const existing = sportMap.get(sport) || { count: 0, value: 0 };
    sportMap.set(sport, {
      count: existing.count + 1,
      value: existing.value + (deal.amount || 0),
    });
  });

  return Array.from(sportMap.entries()).map(([sport, data]) => ({
    sport,
    ...data,
  }));
}

export async function getRiskDistribution(): Promise<RiskDistribution[]> {
  const { data: deals } = await supabase
    .from("deals" as any)
    .select("roc_risk_level");

  if (!deals) return [];

  const riskMap = new Map<string, number>();
  deals.forEach((deal: any) => {
    const level = deal.roc_risk_level || "unknown";
    riskMap.set(level, (riskMap.get(level) || 0) + 1);
  });

  const total = deals.length;
  return Array.from(riskMap.entries()).map(([riskLevel, count]) => ({
    riskLevel,
    count,
    percentage: (count / total) * 100,
  }));
}

export interface AIMetrics {
  totalAIAnalyses: number;
  avgAIRiskScore: number;
  aiHighRiskFlagged: number;
  lastAnalyzedAt: Date | null;
  aiViolationsPrevented: number;
}

export async function getAIMetrics(): Promise<AIMetrics> {
  const { data: deals } = await supabase
    .from("deals" as any)
    .select("ai_risk_score, ai_risk_level, ai_analyzed_at, status");

  if (!deals) {
    return {
      totalAIAnalyses: 0,
      avgAIRiskScore: 0,
      aiHighRiskFlagged: 0,
      lastAnalyzedAt: null,
      aiViolationsPrevented: 0,
    };
  }

  const analyzedDeals = (deals as any[]).filter((d: any) => d.ai_analyzed_at);
  
  const totalAIAnalyses = analyzedDeals.length;
  const avgAIRiskScore = totalAIAnalyses > 0
    ? analyzedDeals.reduce((sum: number, d: any) => sum + (d.ai_risk_score || 0), 0) / totalAIAnalyses
    : 0;

  const aiHighRiskFlagged = analyzedDeals.filter(
    (d: any) => d.ai_risk_level === "high" || d.ai_risk_level === "critical"
  ).length;

  const lastAnalyzedAt = analyzedDeals.length > 0
    ? new Date(Math.max(...analyzedDeals.map((d: any) => new Date(d.ai_analyzed_at).getTime())))
    : null;

  // Count deals where AI flagged high/critical and were rejected
  const aiViolationsPrevented = analyzedDeals.filter(
    (d: any) => (d.ai_risk_level === "high" || d.ai_risk_level === "critical") && d.status === "rejected"
  ).length;

  return {
    totalAIAnalyses,
    avgAIRiskScore: Math.round(avgAIRiskScore),
    aiHighRiskFlagged,
    lastAnalyzedAt,
    aiViolationsPrevented,
  };
}

export interface ComplianceMetricsTrend {
  date: string;
  approvalRate: number;
  avgReviewTimeHours: number;
  aiAccuracy: number;
  totalDeals: number;
}

export async function getComplianceMetricsTrends(startDate: Date, endDate: Date): Promise<ComplianceMetricsTrend[]> {
  const { data: deals } = await supabase
    .from("deals" as any)
    .select("created_at, approved_at, submitted_at, status, ai_risk_level")
    .gte("created_at", startDate.toISOString())
    .lte("created_at", endDate.toISOString())
    .order("created_at", { ascending: true });

  if (!deals) return [];

  // Group by date
  const trendMap = new Map<string, {
    approved: number;
    rejected: number;
    total: number;
    totalReviewTime: number;
    reviewedCount: number;
    aiCorrect: number;
    aiTotal: number;
  }>();
  
  deals.forEach((deal: any) => {
    const date = new Date(deal.created_at).toISOString().split('T')[0];
    const existing = trendMap.get(date) || { 
      approved: 0, 
      rejected: 0, 
      total: 0,
      totalReviewTime: 0,
      reviewedCount: 0,
      aiCorrect: 0,
      aiTotal: 0
    };
    
    existing.total += 1;
    
    if (deal.status === 'active') {
      existing.approved += 1;
    } else if (deal.status === 'rejected') {
      existing.rejected += 1;
    }
    
    // Calculate review time if deal has been reviewed
    if (deal.approved_at && deal.created_at) {
      const reviewTime = new Date(deal.approved_at).getTime() - new Date(deal.created_at).getTime();
      existing.totalReviewTime += reviewTime;
      existing.reviewedCount += 1;
    }
    
    // Calculate AI accuracy (if AI flagged high/critical and deal was rejected, or AI flagged low and deal was approved)
    if (deal.ai_risk_level) {
      existing.aiTotal += 1;
      const aiHighRisk = deal.ai_risk_level === 'high' || deal.ai_risk_level === 'critical';
      const wasRejected = deal.status === 'rejected';
      const wasApproved = deal.status === 'active';
      
      if ((aiHighRisk && wasRejected) || (!aiHighRisk && wasApproved)) {
        existing.aiCorrect += 1;
      }
    }
    
    trendMap.set(date, existing);
  });

  return Array.from(trendMap.entries()).map(([date, data]) => ({
    date,
    approvalRate: data.total > 0 ? (data.approved / data.total) * 100 : 0,
    avgReviewTimeHours: data.reviewedCount > 0 
      ? (data.totalReviewTime / data.reviewedCount) / (1000 * 60 * 60)
      : 0,
    aiAccuracy: data.aiTotal > 0 ? (data.aiCorrect / data.aiTotal) * 100 : 0,
    totalDeals: data.total,
  }));
}

export interface ComplianceComparison {
  current: {
    approvalRate: number;
    avgReviewTimeHours: number;
    aiAccuracy: number;
    totalDeals: number;
  };
  previous: {
    approvalRate: number;
    avgReviewTimeHours: number;
    aiAccuracy: number;
    totalDeals: number;
  };
  changes: {
    approvalRate: number;
    avgReviewTimeHours: number;
    aiAccuracy: number;
    totalDeals: number;
  };
}

export async function getComplianceComparison(currentStart: Date, currentEnd: Date): Promise<ComplianceComparison> {
  // Calculate previous period (same length as current period)
  const periodLength = currentEnd.getTime() - currentStart.getTime();
  const previousStart = new Date(currentStart.getTime() - periodLength);
  const previousEnd = new Date(currentStart.getTime() - 1); // Day before current period starts

  const [currentTrends, previousTrends] = await Promise.all([
    getComplianceMetricsTrends(currentStart, currentEnd),
    getComplianceMetricsTrends(previousStart, previousEnd),
  ]);

  const calculateAverages = (trends: ComplianceMetricsTrend[]) => {
    if (trends.length === 0) {
      return {
        approvalRate: 0,
        avgReviewTimeHours: 0,
        aiAccuracy: 0,
        totalDeals: 0,
      };
    }

    return {
      approvalRate: trends.reduce((sum, t) => sum + t.approvalRate, 0) / trends.length,
      avgReviewTimeHours: trends.reduce((sum, t) => sum + t.avgReviewTimeHours, 0) / trends.length,
      aiAccuracy: trends.reduce((sum, t) => sum + t.aiAccuracy, 0) / trends.length,
      totalDeals: trends.reduce((sum, t) => sum + t.totalDeals, 0),
    };
  };

  const current = calculateAverages(currentTrends);
  const previous = calculateAverages(previousTrends);

  const calculateChange = (curr: number, prev: number) => {
    if (prev === 0) return curr > 0 ? 100 : 0;
    return ((curr - prev) / prev) * 100;
  };

  return {
    current,
    previous,
    changes: {
      approvalRate: calculateChange(current.approvalRate, previous.approvalRate),
      avgReviewTimeHours: calculateChange(current.avgReviewTimeHours, previous.avgReviewTimeHours),
      aiAccuracy: calculateChange(current.aiAccuracy, previous.aiAccuracy),
      totalDeals: calculateChange(current.totalDeals, previous.totalDeals),
    },
  };
}
